import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/lib/utils';
import { getAutoIntelRPOCode } from '../utils/autointel';
import { InventoryRecommendation } from '../utils/autointel/autointel.types';
import { uimetadata, metadata } from '../utils/metadata';
import { useRTL, useTranslation } from '../utils/translations';
// Corvette (Chevrolet) uses the original coloured SVG icons imported as images.
import matchesSelectionIconCorvette from '../assets/icons/homepage/matches_selection_corvette.svg';
import replacesSelectionIconCorvette from '../assets/icons/homepage/replaces_selection_corvette.svg';
import includedIconCorvette from '../assets/icons/homepage/included_corvette.svg';

interface MatchDifferencesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  match?: InventoryRecommendation;
  variantID?: string;
  exteriorColorID?: string;
  interiorColorID?: string;
  packageIDs?: string[];
  accessoriesIDs?: string[];
}

type SpecStatus = 'match' | 'different' | 'missing';

interface ComparisonRow {
  label: string;
  matchValue: string;
  status: SpecStatus;
}

// Close circle X button icon
const CloseCircleIcon: React.FC = () => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" className="shrink-0">
    <circle cx="9.5" cy="9.5" r="9" stroke="currentColor" />
    <path d="M6 6L9.5 9.5M9.5 9.5L13 6M9.5 9.5L6 13M9.5 9.5L13 13" stroke="currentColor" />
  </svg>
);

// ── Status icons (inline SVG with currentColor for brand-awareness) ──────────
// Source: src/assets/icons/homepage/{matches_selection,replaces_selection,included}.svg
// Colors use currentColor so they respect var(--brand-accent) via CSS color.

const MatchesSelectionIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M4.14181 4.23239C-1.41063 9.84522 -1.38044 18.93 4.23239 24.4825C9.84522 30.0349 18.93 30.0047 24.4825 24.3919C30.0349 18.7791 30.0047 9.69426 24.3919 4.14181C18.7791 -1.41063 9.69426 -1.38044 4.14181 4.23239ZM21.2822 11.5661L13.4956 19.4433C12.8314 20.1075 11.7445 20.1075 11.0803 19.4433L10.7784 19.1414L9.87266 18.2356L7.3366 15.7298C6.67239 15.0656 6.67239 13.9787 7.3366 13.3145C8.0008 12.6503 9.08769 12.6503 9.7519 13.3145L12.288 15.8203L18.8669 9.18101C19.5311 8.5168 20.618 8.5168 21.2822 9.18101C21.9464 9.81503 21.9464 10.9019 21.2822 11.5661Z" fill="currentColor"/>
  </svg>
);

const ReplacesSelectionIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M14.5381 0.5C22.2912 0.5 28.5769 6.78503 28.5771 14.5381C28.5771 22.2913 22.2913 28.5771 14.5381 28.5771C6.78503 28.5769 0.5 22.2912 0.5 14.5381C0.500203 6.78516 6.78516 0.500204 14.5381 0.5Z" stroke="currentColor"/>
    <path d="M21 10.5719L16.448 13.6052L16.4332 11.5218H8.01782L8 11.0749L8.00041 10.6622L8.00008 10.3059L8.01486 9.62466H16.4273L16.4361 7.53857L20.997 10.5746L21 10.5719Z" fill="currentColor"/>
    <path d="M7 17.9383L11.552 21.405L11.5668 19.024H19.9822L20 18.5132L19.9996 18.0415L19.9999 17.6344L19.9851 16.8558H11.5727L11.5639 14.4717L7.00296 17.9414L7 17.9383Z" fill="currentColor"/>
  </svg>
);

const IncludedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M14.5381 0.5C22.2912 0.5 28.5769 6.78503 28.5771 14.5381C28.5771 22.2913 22.2913 28.5771 14.5381 28.5771C6.78503 28.5769 0.5 22.2912 0.5 14.5381C0.500203 6.78516 6.78516 0.500204 14.5381 0.5Z" stroke="currentColor"/>
    <path d="M20.383 10.4085H19.6177C19.7647 10.1331 19.8417 9.82492 19.844 9.51211C19.844 9.10594 19.718 8.7091 19.4847 8.37528C19.2513 8.04147 18.9177 7.7917 18.535 7.65397C18.1523 7.51624 17.7347 7.50224 17.3427 7.61195C16.9507 7.72167 16.6007 7.95043 16.344 8.26557C16.3253 8.29358 15.518 9.36271 15 10.0513C14.4797 9.36038 13.6723 8.28658 13.649 8.25857C13.39 7.94576 13.0423 7.71933 12.6503 7.60962C12.2583 7.50224 11.843 7.51624 11.4603 7.65397C11.0777 7.7917 10.7463 8.04381 10.513 8.37528C10.2797 8.70676 10.1537 9.1036 10.1537 9.50978C10.1537 9.82258 10.233 10.1307 10.38 10.4062H9.61467C9.18767 10.4062 8.777 10.5766 8.47367 10.88C8.17033 11.1835 8 11.592 8 12.0215V14.531C8 14.6734 8.056 14.8111 8.15867 14.9115C8.259 15.0119 8.39667 15.0679 8.539 15.0679H8.71867V18.8332C8.67667 19.1973 8.71867 19.5638 8.83767 19.9093C8.959 20.2548 9.155 20.5676 9.414 20.8267C9.673 21.0858 9.988 21.2819 10.3333 21.4033C10.6787 21.5247 11.0473 21.5644 11.4113 21.5224H18.5887C18.9527 21.5644 19.3213 21.5224 19.6667 21.4033C20.012 21.2843 20.327 21.0858 20.586 20.8267C20.845 20.5676 21.041 20.2548 21.1623 19.9093C21.2837 19.5638 21.3233 19.195 21.2813 18.8332V15.0679H21.461C21.6033 15.0679 21.741 15.0119 21.8413 14.9115C21.9417 14.8111 22 14.6734 22 14.531V12.0215C22 11.5944 21.8297 11.1835 21.5263 10.88C21.223 10.5766 20.8123 10.4062 20.3853 10.4062L20.383 10.4085ZM20.9197 12.0239V13.9964H15.5367V11.487H20.3807C20.523 11.487 20.6607 11.543 20.761 11.6457C20.8613 11.7461 20.9197 11.8838 20.9197 12.0262V12.0239ZM17.1793 8.94253C17.261 8.83982 17.3683 8.75579 17.4873 8.69976C17.6063 8.64374 17.737 8.61339 17.87 8.61572C18.108 8.61572 18.3367 8.70909 18.5047 8.87717C18.6727 9.04524 18.7683 9.27401 18.7683 9.51211C18.7683 9.75022 18.6727 9.97898 18.5047 10.1471C18.3367 10.3151 18.108 10.4085 17.87 10.4085H16.0757C16.561 9.76422 17.1467 8.98688 17.1793 8.94253ZM11.2317 9.51211C11.2317 9.27401 11.3273 9.04758 11.4953 8.87951C11.6633 8.71143 11.892 8.61572 12.13 8.61572C12.2607 8.61572 12.389 8.6414 12.5057 8.69743C12.6223 8.75112 12.7273 8.83282 12.809 8.9332C12.8463 8.98455 13.4367 9.76422 13.922 10.4108H12.1277C11.8897 10.4108 11.661 10.3151 11.493 10.1471C11.325 9.97898 11.2293 9.75022 11.2293 9.51445L11.2317 9.51211ZM9.078 12.0239C9.078 11.8815 9.134 11.7438 9.23667 11.6434C9.337 11.543 9.47467 11.487 9.617 11.4846H14.461V13.9941H9.078V12.0215V12.0239ZM9.79433 18.8355V15.0702H14.4587V20.4486H11.409C10.2773 20.4486 9.79433 19.9654 9.79433 18.8355ZM20.2033 18.8355C20.2033 19.9677 19.7203 20.4486 18.5887 20.4486H15.539V15.0702H20.2033V18.8355Z" fill="currentColor"/>
  </svg>
);

const StatusIcon: React.FC<{ status: SpecStatus; className?: string }> = ({ status, className }) => {
  const isCadillac = (uimetadata.config as any).brand === 'cadillac';
  const iconClassName = cn('shrink-0 w-[20px] h-[20px]', className);

  // Cadillac: use inline SVG with currentColor (brand-aware)
  if (isCadillac) {
    if (status === 'match') return <MatchesSelectionIcon className={iconClassName} />;
    if (status === 'different') return <ReplacesSelectionIcon className={iconClassName} />;
    return <IncludedIcon className={iconClassName} />;
  }

  // Chevrolet (Corvette): use original coloured SVG icons as images
  const src = status === 'match'
    ? matchesSelectionIconCorvette
    : status === 'different'
    ? replacesSelectionIconCorvette
    : includedIconCorvette;
  return <img src={src} alt="" aria-hidden="true" className={iconClassName} />;
};

// Legend icons — larger (25px) version for the legend section, same brand logic
const LegendIcon: React.FC<{ status: SpecStatus }> = ({ status }) => {
  const isCadillac = (uimetadata.config as any).brand === 'cadillac';
  const className = 'shrink-0 w-[25px] h-[25px] text-[var(--brand-accent)]';

  if (isCadillac) {
    if (status === 'match') return <MatchesSelectionIcon className={className} />;
    if (status === 'different') return <ReplacesSelectionIcon className={className} />;
    return <IncludedIcon className={className} />;
  }

  const src = status === 'match'
    ? matchesSelectionIconCorvette
    : status === 'different'
    ? replacesSelectionIconCorvette
    : includedIconCorvette;
  return <img src={src} alt="" aria-hidden="true" className={className} />;
};

export const MatchDifferencesPanel: React.FC<MatchDifferencesPanelProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  match,
  variantID,
  exteriorColorID,
  interiorColorID,
  packageIDs = [],
  accessoriesIDs = [],
}) => {
  const modelId = uimetadata.config.model;
  const modelData = (uimetadata.models as any)[modelId];
  const { isRTL } = useRTL();
  const { t } = useTranslation();

  const variantLabel = isRTL
    ? (modelData?.variants?.[variantID ?? '']?.name?.ar ?? modelData?.variants?.[variantID ?? '']?.name?.en ?? variantID ?? '')
    : (modelData?.variants?.[variantID ?? '']?.name?.en ?? variantID ?? '');
  const extColorLabel = isRTL
    ? (modelData?.colors?.exterior?.[exteriorColorID ?? '']?.label?.ar ?? modelData?.colors?.exterior?.[exteriorColorID ?? '']?.label?.en ?? exteriorColorID ?? '')
    : (modelData?.colors?.exterior?.[exteriorColorID ?? '']?.label?.en ?? exteriorColorID ?? '');
  const intColorLabel = isRTL
    ? (modelData?.colors?.interior?.[interiorColorID ?? '']?.label?.ar ?? modelData?.colors?.interior?.[interiorColorID ?? '']?.label?.en ?? interiorColorID ?? '')
    : (modelData?.colors?.interior?.[interiorColorID ?? '']?.label?.en ?? interiorColorID ?? '');

  // Values are the same when their RPO codes match OR their display names match — the code
  // systems don't always line up, so a name match still means "matches your selection".
  const nameEq = (a?: string, b?: string) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

  const buildRows = (): ComparisonRow[] => {
    if (!match) return [];
    const rows: ComparisonRow[] = [];

    // Trim
    const userTrimRPO = getAutoIntelRPOCode(variantID ?? '');
    const matchTrimCode = match.trim?.code ?? '';
    const matchTrimName = match.trim?.name ?? '';
    rows.push({
      label: t('staticTranslations.localMatches.specTrim', 'Trim'),
      matchValue: matchTrimName || variantLabel,
      status:
        (matchTrimCode && userTrimRPO && matchTrimCode === userTrimRPO) || nameEq(matchTrimName, variantLabel)
          ? 'match'
          : matchTrimName
          ? 'different'
          : 'match',
    });

    // Exterior Color
    const userExtRPO = getAutoIntelRPOCode(exteriorColorID ?? '');
    const matchPaintCode = match.paint?.code ?? '';
    const matchPaintName = match.paint?.name ?? '';
    rows.push({
      label: t('staticTranslations.localMatches.specExteriorColor', 'Exterior Color'),
      matchValue: matchPaintName || extColorLabel,
      status:
        (matchPaintCode && userExtRPO && matchPaintCode === userExtRPO) || nameEq(matchPaintName, extColorLabel)
          ? 'match'
          : matchPaintCode && userExtRPO
          ? 'different'
          : 'match',
    });

    // Interior Color — RPO depends on the model + seat trim, so resolve it per variant.
    const userIntRPO = getAutoIntelRPOCode(interiorColorID ?? '', variantID ?? '');
    const matchFabricCode = match.fabric?.code ?? '';
    const matchFabricName = match.fabric?.name ?? '';
    rows.push({
      label: t('staticTranslations.localMatches.specInteriorColor', 'Interior Color'),
      matchValue: matchFabricName || intColorLabel,
      status:
        (matchFabricCode && userIntRPO && matchFabricCode === userIntRPO) || nameEq(matchFabricName, intColorLabel)
          ? 'match'
          : matchFabricCode && userIntRPO
          ? 'different'
          : 'match',
    });

    // Wheels
    const matchWheelName = match.wheel?.name ?? '';
    if (matchWheelName) {
      rows.push({ label: t('staticTranslations.localMatches.specWheels', 'Wheels'), matchValue: matchWheelName, status: 'match' });
    }

    // Packages are NOT shown here — they're listed in the "Comes with vehicle" section.

    return rows;
  };

  const rows = buildRows();

  // Build set of RPO codes from ALL applied accessories + packages (exterior, interior, options tabs).
  const appliedRPOs = React.useMemo(() => {
    const set = new Set<string>();
    [...accessoriesIDs, ...packageIDs].forEach((id) => {
      const rpo = getAutoIntelRPOCode(id);
      if (rpo) set.add(rpo);
    });
    return set;
  }, [accessoriesIDs, packageIDs]);

  // Build RPO -> {isDefault, display} from graphics metadata (used for filtering hidden items).
  const rpoToGraphicsFlags = React.useMemo(() => {
    const map = new Map<string, { isDefault: boolean; display: boolean }>();
    const graphicsModel = (metadata as any)?.models?.[modelId];
    if (!graphicsModel) return map;
    (['accessories', 'packages'] as const).forEach((section) => {
      if (!graphicsModel[section]) return;
      Object.entries(graphicsModel[section]).forEach(([id, entry]: [string, any]) => {
        const rpo = getAutoIntelRPOCode(id);
        if (rpo && !map.has(rpo)) {
          map.set(rpo, { isDefault: !!entry.isDefault, display: !!entry.display });
        }
      });
    });
    return map;
  }, [modelId]);

  // Build ID -> {isDefault, display} directly from graphics metadata. Some default items
  // (e.g. the Roof) have no RPO code, so the RPO map above can't catch them — filter by id.
  const idToGraphicsFlags = React.useMemo(() => {
    const map = new Map<string, { isDefault: boolean; display: boolean }>();
    const graphicsModel = (metadata as any)?.models?.[modelId];
    if (!graphicsModel) return map;
    (['accessories', 'packages'] as const).forEach((section) => {
      if (!graphicsModel[section]) return;
      Object.entries(graphicsModel[section]).forEach(([id, entry]: [string, any]) => {
        map.set(id, { isDefault: !!entry.isDefault, display: !!entry.display });
      });
    });
    return map;
  }, [modelId]);

  // Build name lookup and presence set from match.options (recommendations API).
  const matchOptionsData = React.useMemo(() => {
    const nameByRPO = new Map<string, string>();
    const rpoSet = new Set<string>();
    const nameSet = new Set<string>(); // lowercased option names, for code-independent matching
    (match?.options ?? []).forEach((opt: any) => {
      const code: string = opt.code ?? '';
      if (opt.name) nameSet.add(String(opt.name).trim().toLowerCase());
      if (!code) return;
      rpoSet.add(code);
      if (opt.name) nameByRPO.set(code, opt.name as string);
    });
    return { nameByRPO, rpoSet, nameSet };
  }, [match]);

  // Build ID -> label from UI metadata for name fallback.
  const metadataNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    const model = (uimetadata as any).models?.[modelId];
    if (!model) return map;
    (['accessories', 'packages'] as const).forEach((section) => {
      if (!model[section]) return;
      Object.entries(model[section]).forEach(([id, item]: [string, any]) => {
        map.set(id, item.label?.en ?? item.name?.en ?? id);
      });
    });
    return map;
  }, [modelId]);

  // Accessory ids that belong to an applied package — shown as the package (not individually).
  const packageMemberAccIds = React.useMemo(() => {
    const set = new Set<string>();
    const pkgs = (metadata as any)?.models?.[modelId]?.packages ?? {};
    (packageIDs ?? []).forEach((pid) => {
      (pkgs[pid]?.accessoryIds ?? []).forEach((aid: string) => set.add(aid));
    });
    return set;
  }, [packageIDs, modelId]);

  // Show ALL user-applied options with status:
  //   'match'   — user applied it AND the recommendation has it
  //   'missing' — user applied it BUT the recommendation does not
  // Excludes any isDefault:true item (default accessories/packages) and hidden-prefix names.
  const installedOptions = React.useMemo(() => {
    const hiddenPrefixes = ['WO_', 'W_Midnight', 'Variant_Parts'];
    return [...accessoriesIDs, ...packageIDs]
      .map((id) => {
        // If this accessory is part of an applied package, don't list it individually —
        // the package name (from packageIDs) already represents it.
        if (packageMemberAccIds.has(id)) return null;
        const rpo = getAutoIntelRPOCode(id);
        // Filter: skip any default accessory/package (isDefault:true) — defaults are not
        // shown in the "Comes with vehicle" list regardless of display or RPO. Check by id
        // (direct) and by RPO (fallback) since some defaults have no RPO code.
        const flags = (rpo ? rpoToGraphicsFlags.get(rpo) : undefined) ?? idToGraphicsFlags.get(id);
        if (idToGraphicsFlags.get(id)?.isDefault === true || flags?.isDefault === true) return null;
        // Resolve name: API name > UI metadata label
        const name = (rpo ? matchOptionsData.nameByRPO.get(rpo) : undefined) ?? metadataNameById.get(id);
        if (!name) return null;
        // Filter hidden prefixes
        if (hiddenPrefixes.some((p) => name.startsWith(p))) return null;
        // "Comes with vehicle" only lists options the vehicle actually has (present in the
        // recommendation), matched by RPO code OR name. Applied options not in the
        // recommendation are omitted entirely.
        const inRecommendation =
          (rpo && matchOptionsData.rpoSet.has(rpo)) || matchOptionsData.nameSet.has(name.trim().toLowerCase());
        if (!inRecommendation) return null;
        return { name, id, status: 'match' as SpecStatus };
      })
      .filter((opt): opt is NonNullable<typeof opt> => opt !== null);
  }, [accessoriesIDs, packageIDs, rpoToGraphicsFlags, idToGraphicsFlags, matchOptionsData, metadataNameById, packageMemberAccIds]);

  const bg = isDarkMode ? 'bg-[#111111]' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-[#333334]';
  const divider = isDarkMode ? 'bg-white/10' : 'bg-[#e8e8e8]';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[20000]"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: isRTL ? '-100%' : '100%' }}
            animate={{ x: 0 }}
            exit={{ x: isRTL ? '-100%' : '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={cn(
              'fixed top-0 h-full w-full md:w-[480px] z-[20001] shadow-2xl overflow-hidden flex flex-col',
              isRTL ? 'left-0' : 'right-0',
              bg
            )}
          >
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto no-scrollbar">
              <div className="px-[36px] py-[49px] flex flex-col gap-[44px]">

                {/* Legend block: title row + legend items */}
                <div className="flex flex-col gap-[19px] w-full">

                  {/* Title + close button row */}
                  <div className="flex items-center justify-between w-full">
                    <p className={cn(
                      'font-sans font-bold text-[20px] leading-none',
                      textPrimary
                    )}>
                      {t('staticTranslations.localMatches.closeMatchDetails', 'Close Match Details')}
                    </p>
                    <button
                      onClick={onClose}
                      className={cn('flex items-center justify-center transition-opacity hover:opacity-70', textPrimary)}
                      aria-label="Close"
                    >
                      <CloseCircleIcon />
                    </button>
                  </div>

                  {/* Legend items */}
                  <div className="flex flex-col gap-[16px]">
                    {/* Matches your selection */}
                    <div className="flex items-center gap-[16px]">
                      <LegendIcon status="match" />
                      <div className="flex flex-col gap-[2px]">
                        <span className={cn('font-sans font-bold text-[16px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.matchesYourSelection', 'Matches your selection')}
                        </span>
                        <span className={cn('font-sans font-normal text-[14px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.sameAsWhatYouSelected', 'Same as what you selected')}
                        </span>
                      </div>
                    </div>
                    {/* Replaces your selection */}
                    <div className="flex items-center gap-[16px]">
                      <LegendIcon status="different" />
                      <div className="flex flex-col gap-[2px]">
                        <span className={cn('font-sans font-bold text-[16px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.replacesYourSelection', 'Replaces your selection')}
                        </span>
                        <span className={cn('font-sans font-normal text-[14px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.selectionNotIncluded', 'Selection not included')}
                        </span>
                      </div>
                    </div>
                    {/* Included in this vehicle */}
                    <div className="flex items-center gap-[16px]">
                      <LegendIcon status="missing" />
                      <div className="flex flex-col gap-[2px]">
                        <span className={cn('font-sans font-bold text-[16px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.includedInThisVehicle', 'Included in this vehicle')}
                        </span>
                        <span className={cn('font-sans font-normal text-[14px] leading-[20px] tracking-[0.35px]', textPrimary)}>
                          {t('staticTranslations.localMatches.additionalFeatureAlreadyIncluded', 'Additional feature already included')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer note */}
                  <p className={cn('font-sans font-normal text-[14px] leading-[20px] tracking-[0.35px]', isDarkMode ? 'text-white/60' : 'text-[#555]')}>
                    {t('staticTranslations.localMatches.stockDisclaimer', 'Available stock may include extra features not selected in your original configuration.')}
                  </p>
                </div>

                {/* Details block: Vehicle Details + Installed Options */}
                <div className="flex flex-col gap-[24px] w-full">

                  {/* Vehicle Details section */}
                  <div className="flex flex-col gap-[24px] w-full">
                    <p className={cn(
                      'font-sans font-bold text-[20px] leading-none',
                      textPrimary
                    )}>
                      {t('staticTranslations.localMatches.vehicleDetails', 'Vehicle Details')}
                    </p>

                    <div className="flex flex-col gap-[15px] w-full">
                      {rows.map((row, idx) => (
                        <div key={idx} className="flex flex-col gap-[15px]">
                          <div className="flex items-start justify-between w-full">
                            <div className="flex gap-[4px] items-start flex-1 pr-3">
                              <span className={cn(
                                'font-sans font-medium text-[14px] leading-[20px] shrink-0',
                                textPrimary
                              )}>
                                {row.label}:
                              </span>
                              <span className={cn(
                                'font-sans font-normal text-[14px] leading-[20px]',
                                textPrimary
                              )}>
                                {row.matchValue}
                              </span>
                            </div>
                            <StatusIcon status={row.status} className="text-[var(--brand-accent)]" />
                          </div>
                          <div className={cn('h-px w-full', divider)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Comes with vehicle section */}
                  {installedOptions.length > 0 && (
                    <div className="flex flex-col gap-[24px] w-full">
                      <p className={cn(
                        'font-sans font-bold text-[20px] leading-none',
                        textPrimary
                      )}>
                        {t('staticTranslations.localMatches.installedOptionsTitle', 'Comes with vehicle')}
                      </p>

                      <div className="flex flex-col gap-[15px] w-full">
                        {installedOptions.map((opt) => (
                          <div key={opt.id} className="flex flex-col gap-[15px]">
                            <div className="flex items-center justify-between w-full">
                              <span className={cn(
                                'font-sans font-medium text-[14px] leading-[20px] tracking-[0.35px] flex-1 pr-3',
                                textPrimary
                              )}>
                                {opt.name}
                              </span>
                              <StatusIcon status={opt.status} className="text-[var(--brand-accent)]" />
                            </div>
                            <div className={cn('h-px w-full', divider)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

